import { useSyncExternalStore } from "react";
import { inclFromExcl, type Cents } from "@glastotaal/core";
import { translate } from "@/lib/i18n";

/**
 * Btw-weergave (puur weergave — bedragen blijven excl. btw opgeslagen).
 * Particulier ziet standaard INCL. btw; een zakelijk account kan in z'n
 * voorkeuren op EXCL. zetten, en dat geldt dan sitebreed. De voorkeur staat in
 * localStorage (instant + werkt voor gasten) en wordt bij inloggen uit het
 * account gesynct (zie CustomerAuthProvider).
 */
export type BtwPref = "incl" | "excl";

const KEY = "glastotaal.btwpref";

function read(): BtwPref {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "incl" || v === "excl") return v;
  } catch {
    /* geen storage */
  }
  return "incl";
}

let current: BtwPref = read();
const listeners = new Set<() => void>();

/** Voorkeur zetten (en sitebreed laten doorwerken). */
export function setBtwPreference(p: BtwPref): void {
  if (p === current) return;
  current = p;
  try {
    localStorage.setItem(KEY, p);
  } catch {
    /* geen storage */
  }
  listeners.forEach((l) => l());
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Reactieve btw-voorkeur. Tuple-API blijft gelijk: [pref, setPref]. */
export function useBtwPreference(): [BtwPref, (p: BtwPref) => void] {
  const pref = useSyncExternalStore(subscribe, () => current, () => current);
  return [pref, setBtwPreference];
}

/** Excl.-stored cents → the amount to display for the chosen preference. */
export function btwAmount(exclCents: Cents, pref: BtwPref): Cents {
  return pref === "incl" ? inclFromExcl(exclCents) : exclCents;
}

/** Prijssuffix. Vertaalt zichzelf: dit label staat op ~75 plekken achter een bedrag, en die
 *  overal met t() omwikkelen zou het bij de eerstvolgende nieuwe prijsweergave weer misgaan. */
export const btwLabel = (pref: BtwPref): string => translate(pref === "incl" ? "incl. btw" : "excl. btw");

/** Order-eindtotaal in de gekozen btw-basis. Orders bewaren het INCL.-totaal + het btw-bedrag,
 *  dus het excl.-totaal = total − vat. Zo tonen account/bestellingen hetzelfde als de rest van
 *  de site (zakelijk = excl.), consistent met de korting en de jaaromzet. */
export function orderTotalForPref(totals: { total: number; vat: number }, pref: BtwPref): Cents {
  return (pref === "incl" ? totals.total : totals.total - totals.vat) as Cents;
}
